/* eslint-disable no-console */
import { getCurrentUser } from '../../services/userRole'
import { fetchUserAccountData } from '../CopUserAccount/service/CopUserAccountService'
import { hideRedirectLoader, showRedirectLoader } from './redirectLoader'
import { waitForSessionSync } from './sessionSync'

const INTERNAL_HOME_PREFIX = '/content/powerportal/www/en/home'
const REDIRECT_GUARD_KEY = 'cop:last-redirect-target'
const REDIRECT_GUARD_WINDOW_MS = 8000
const REDIRECT_DEBUG_QUERY_PARAM = 'copRedirectDebug'
const REDIRECT_DEBUG_SESSION_KEY = 'cop:redirect-debug'
const CUSTOMER_ID_KEY = 'cop:customer-id'
const CUSTOMER_NAME_KEY = 'cop:customer-name'
const CURRENT_USER_KEY = 'cop:current-user'

const isRedirectDebugEnabled = (): boolean => {
    try {
        const query = new URLSearchParams(window.location.search)
        const fromQuery = query.get(REDIRECT_DEBUG_QUERY_PARAM)
        if (fromQuery === '1' || fromQuery === 'true') return true

        const fromSession = sessionStorage.getItem(REDIRECT_DEBUG_SESSION_KEY)
        return fromSession === '1' || fromSession === 'true'
    } catch {
        return false
    }
}

const logRedirectDebug = (message: string, details?: Record<string, unknown>): void => {
    if (!isRedirectDebugEnabled()) return
    if (details) {
        return
    }
}

const normalizePathname = (pathname: string): string => {
    let path = (pathname || '').split('?')[0].split('#')[0].trim()
    if (!path.startsWith('/')) path = `/${path}`
    if (path.startsWith(INTERNAL_HOME_PREFIX)) {
        path = path.slice(INTERNAL_HOME_PREFIX.length) || '/'
        if (!path.startsWith('/')) path = `/${path}`
    }

    path = path.replace(/\.html$/i, '')
    while (path.length > 1 && path.endsWith('/')) {
        path = path.slice(0, -1)
    }
    return path
}

const hasRecentRedirectTo = (target: string): boolean => {
    try {
        const raw = sessionStorage.getItem(REDIRECT_GUARD_KEY)
        if (!raw) return false

        const parsed = JSON.parse(raw) as { target?: string; at?: number }
        if (parsed.target !== target || typeof parsed.at !== 'number') return false

        return (Date.now() - parsed.at) < REDIRECT_GUARD_WINDOW_MS
    } catch {
        return false
    }
}

const markRedirectTarget = (target: string): void => {
    try {
        sessionStorage.setItem(REDIRECT_GUARD_KEY, JSON.stringify({ target, at: Date.now() }))
    } catch {
        // Non-fatal; guard is best-effort.
    }
}

const shouldRedirect = (targetUrl: URL): boolean => {
    const current = normalizePathname(window.location.pathname)
    const target = normalizePathname(targetUrl.pathname)
    const absoluteTarget = targetUrl.href

    logRedirectDebug('Evaluating redirect decision.', {
        currentPathname: window.location.pathname,
        targetPathname: targetUrl.pathname,
        normalizedCurrent: current,
        normalizedTarget: target,
        absoluteTarget,
    })

    if (current === target) {
        logRedirectDebug('Skipping redirect: current and target path are equivalent after normalization.')
        return false
    }
    if (target !== '/' && current.startsWith(`${target}/`)) {
        logRedirectDebug('Skipping redirect: current path is already under target path.', {
            normalizedCurrent: current,
            normalizedTarget: target,
        })
        return false
    }

    if (hasRecentRedirectTo(absoluteTarget)) {
        logRedirectDebug('Skipping redirect: recent redirect guard is active.', {
            guardKey: REDIRECT_GUARD_KEY,
            guardWindowMs: REDIRECT_GUARD_WINDOW_MS,
            absoluteTarget,
        })
        return false
    }

    markRedirectTarget(absoluteTarget)
    logRedirectDebug('Redirect approved.', { absoluteTarget })
    return true
}

const redirectTo = (path: string): boolean => {
    if (!path) return false
    try {
        const targetUrl = new URL(path, window.location.href)
        logRedirectDebug('redirectTo invoked.', {
            requestedPath: path,
            currentUrl: window.location.href,
            resolvedTarget: targetUrl.href,
        })
        if (shouldRedirect(targetUrl)) {
            logRedirectDebug('Executing window.location.assign().', {
                assignTarget: path,
            })
            window.location.assign(path)
            return true
        }
        return false
    } catch {
        const targetPath = path.split('?')[0]
        const fallbackTarget = normalizePathname(targetPath)
        const current = normalizePathname(window.location.pathname)
        logRedirectDebug('redirectTo fallback path evaluation.', {
            requestedPath: path,
            fallbackTarget,
            normalizedCurrent: current,
        })
        if (current !== fallbackTarget && !(fallbackTarget !== '/' && current.startsWith(`${fallbackTarget}/`))) {
            logRedirectDebug('Executing window.location.assign() via fallback branch.', {
                assignTarget: path,
            })
            window.location.assign(path)
            return true
        } else {
            logRedirectDebug('Skipping redirect in fallback branch.')
            return false
        }
    }
}

export const persistCustomerSelection = (id: string, name: string): void => {
    try {
        sessionStorage.setItem(CUSTOMER_ID_KEY, id)
        sessionStorage.setItem(CUSTOMER_NAME_KEY, name)
        // Clear stale permissions so the new customer's permissions are fetched fresh
        sessionStorage.removeItem('cop:permissions')
        // Notify powerportalPermissions.js so it can trigger the PermissionsV2 fetch
        window.dispatchEvent(new CustomEvent('cop:customer-ready', { detail: { customerId: id } }))
    } catch {
        // Non-fatal.
    }
}

export const clearCustomerSelection = (): void => {
    try {
        sessionStorage.removeItem(CUSTOMER_ID_KEY)
        sessionStorage.removeItem(CUSTOMER_NAME_KEY)
    } catch {
        // Non-fatal.
    }
}

const handleAdminRedirect = (customerId: string | null, adminAccountSelectPath: string): void => {
    if (customerId) { hideRedirectLoader(); return }
    if (!redirectTo(adminAccountSelectPath)) hideRedirectLoader()
}

const handleUserManagerRedirect = (homePagePath: string): void => {
    const isAlreadySetup = Boolean(sessionStorage.getItem(CUSTOMER_ID_KEY))
    persistCustomerSelection('NOSLA', 'No SLA')
    if (isAlreadySetup) { hideRedirectLoader(); return }
    if (!redirectTo(homePagePath)) hideRedirectLoader()
}

// New-tab of an active session: consumers of cop:current-user (country selector, admin-mode button)
// read it synchronously at mount, before this async flow populates it. A one-shot reload lets them
// re-render with the correct role. First-login users skip this because customerId is not yet set.
const shouldReloadForNewTabHandoff = (currentUserWasEmpty: boolean, customerId: string | null): boolean =>
    currentUserWasEmpty && Boolean(customerId)

const autoSelectSingleTenant = async (adminAccountSelectPath: string, homePagePath: string): Promise<void> => {
    try {
        const customerData = await fetchUserAccountData()
        const singleCustomer = customerData?.value?.[0]
        if (singleCustomer) {
            persistCustomerSelection(singleCustomer.id, singleCustomer.name)
            if (!redirectTo(homePagePath)) hideRedirectLoader()
            return
        }
        if (!redirectTo(adminAccountSelectPath)) hideRedirectLoader()
    } catch {
        // Fetch failed — fall back to switch account page
        if (!redirectTo(adminAccountSelectPath)) hideRedirectLoader()
    }
}

export const fetchCurrentUserAndRedirect = async (
    adminAccountSelectPath: string,
    homePagePath: string,
): Promise<void> => {
    // Do not run any redirect logic in AEM author mode
    const w = window as unknown as { Granite?: { author?: unknown } }
    if (w.Granite?.author) return

    showRedirectLoader()
    try {
        const currentUserWasEmpty = !sessionStorage.getItem(CURRENT_USER_KEY)
        const user = await getCurrentUser()
        if (!user) { hideRedirectLoader(); return }

        sessionStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user))
        // Ensure the BroadcastChannel handoff has finished so customer-id is populated in the new-tab case
        await waitForSessionSync()
        const customerId = sessionStorage.getItem(CUSTOMER_ID_KEY)

        if (shouldReloadForNewTabHandoff(currentUserWasEmpty, customerId)) {
            window.location.reload()
            return
        }
        logRedirectDebug('User and path context loaded.', {
            adminAccountSelectPath,
            homePagePath,
            userRole: { isSystemAdmin: user.isSystemAdmin, isUserManager: user.isUserManager },
            hasCustomerId: Boolean(customerId),
        })

        if (user.isSystemAdmin) { handleAdminRedirect(customerId, adminAccountSelectPath); return }
        if (user.isUserManager) { handleUserManagerRedirect(homePagePath); return }
        if (customerId) { hideRedirectLoader(); return } // customer already selected — nothing to do

        if (user.isMultiTenantUser) {
            if (!redirectTo(adminAccountSelectPath)) hideRedirectLoader()
        } else if (user.isAssignedToAnyTenant) {
            await autoSelectSingleTenant(adminAccountSelectPath, homePagePath)
        } else {
            hideRedirectLoader() // not assigned to any tenant — no redirect
        }
    } catch (err) {
        hideRedirectLoader()
        console.warn('[CopTokenAuth] fetchCurrentUserAndRedirect failed:', err) // Non-fatal
    }
}